import { getCache, getCacheConfig } from '@ms/yammer-libs-cache';

import type { ManagedApiRequest } from './types';

const slowFetchTimeoutThreshold = 1000;

export type ManagedApiCachingStrategy =
  | 'FetchAndWriteCache'
  | 'FetchOnly'
  | 'ReadCacheOrFetch'
  | 'ReadCacheOrFetchWithTimeout'
  | 'ReadCacheOrFetchAndWrite'
  | 'ReadCacheAndRevalidate'
  | 'ReadCacheAndRevalidateWithFulfill'
  | 'ReadCacheAndRevalidateWithDeferredFulfill'
  | 'ReadCacheOrFetchWithTimeoutDeferredFulfill';

export interface ManagedApiCaching {
  readonly strategy: ManagedApiCachingStrategy;
  readonly key: string;
  readonly ttlInSeconds?: number;
  readonly cacheHitCallback?: () => void;
  readonly cacheUpdateCallback?: () => void;
}

export interface ManagedApiExecutorResult<TResponse> {
  readonly response: TResponse;
  readonly cacheHit: boolean | undefined;
}

export interface ManagedApiCachingExecutorOptions<TResponse> {
  readonly request: ManagedApiRequest;
  readonly caching?: ManagedApiCaching;
  readonly executeRequest: (request: ManagedApiRequest) => Promise<TResponse>;
  readonly responseWritten?: (response: TResponse, cacheKey: string) => void;
}

type ReadCache<TResponse> = () => Promise<TResponse | null>;
type WriteCache<TResponse> = (response: TResponse) => Promise<void>;
type FetchResponse<TResponse> = () => Promise<TResponse>;
type ExecuteCachingStrategy = <TResponse>(
  readCache: ReadCache<TResponse>,
  writeCache: WriteCache<TResponse>,
  fetchResponse: FetchResponse<TResponse>
) => Promise<TResponse>;

const fetchAndWriteCache: ExecuteCachingStrategy = async (_readCache, writeCache, fetchResponse) => {
  const response = await fetchResponse();
  await writeCache(response);

  return response;
};

const fetchOnly: ExecuteCachingStrategy = async (_readCache, _writeCache, fetchResponse) => fetchResponse();

const readCacheOrFetch: ExecuteCachingStrategy = async (readCache, _writeCache, fetchResponse) =>
  (await readCache()) ?? fetchResponse();

const readCacheAndRevalidate: ExecuteCachingStrategy = async (readCache, writeCache, fetchResponse) => {
  const cachedResponse = await readCache();
  if (!cachedResponse) {
    return fetchAndWriteCache(readCache, writeCache, fetchResponse);
  }

  requestIdleCallback(() => {
    void fetchResponse().then(writeCache);
  });

  return cachedResponse;
};

const readCacheOrFetchWithTimeout: ExecuteCachingStrategy = async (readCache, writeCache, fetchResponse) => {
  const fetchPromise = fetchResponse().then(async (response) => {
    await writeCache(response);

    return response;
  });
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, slowFetchTimeoutThreshold);
  });
  const response = await Promise.race([fetchPromise, timeoutPromise]);
  if (response != null) {
    return response;
  }

  const cachedResponse = await readCache();
  if (cachedResponse) {
    return cachedResponse;
  }

  return fetchPromise;
};

const readCacheOrFetchAndWrite: ExecuteCachingStrategy = async (readCache, writeCache, fetchResponse) => {
  const cachedResponse = await readCache();
  if (!cachedResponse) {
    return fetchAndWriteCache(readCache, writeCache, fetchResponse);
  }

  return cachedResponse;
};

const cachingStrategies: Record<ManagedApiCachingStrategy, ExecuteCachingStrategy> = {
  FetchAndWriteCache: fetchAndWriteCache,
  FetchOnly: fetchOnly,
  ReadCacheOrFetch: readCacheOrFetch,
  ReadCacheOrFetchWithTimeout: readCacheOrFetchWithTimeout,
  ReadCacheOrFetchWithTimeoutDeferredFulfill: readCacheOrFetchWithTimeout,
  ReadCacheOrFetchAndWrite: readCacheOrFetchAndWrite,
  ReadCacheAndRevalidate: readCacheAndRevalidate,
  ReadCacheAndRevalidateWithFulfill: readCacheAndRevalidate,
  ReadCacheAndRevalidateWithDeferredFulfill: readCacheAndRevalidate,
};

export const executeManagedApiCaching = async <TResponse>({
  request,
  caching,
  executeRequest,
  responseWritten,
}: ManagedApiCachingExecutorOptions<TResponse>): Promise<ManagedApiExecutorResult<TResponse>> => {
  const cache = getCache();
  let cacheHit: boolean | undefined;

  if (!caching) {
    return { response: await executeRequest(request), cacheHit };
  }

  const readCache: ReadCache<TResponse> = async () => {
    if (!cache) {
      return null;
    }

    const response = await cache.getItem<TResponse>(caching.key);
    cacheHit = Boolean(response);
    if (response) {
      caching.cacheHitCallback?.();
    }

    return response;
  };
  const writeCache: WriteCache<TResponse> = async (response) => {
    if (!cache) {
      return;
    }

    await cache.setItem(caching.key, response, caching.ttlInSeconds ?? getCacheConfig().ttlInSeconds);
    responseWritten?.(response, caching.key);
    caching.cacheUpdateCallback?.();
  };
  const response = await cachingStrategies[caching.strategy](readCache, writeCache, () => executeRequest(request));

  return { response, cacheHit };
};
